// apps/mobile/src/app/services/authService.ts
import apiClient, { API_URL } from './apiClient';
import type { ApiRequestOptions } from './apiClient';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  title?: string;
  company?: string;
  role: string;
  bio?: string;
  avatar_url?: string;
  linkedin?: string;
  portfolio?: string;
  interests?: string[];
  is_verified?: boolean;
  profile_complete?: boolean;
  push_token?: string;
  show_profile?: boolean;
}

export interface PolicyConfirmationsValue {
  adultAttested: boolean;
  termsAccepted: boolean;
}

export interface PolicyStatus {
  current_terms_version: string;
  accepted: boolean;
  accepted_at: string | null;
  adult_attested_at: string | null;
}

export function normalizeAccountEmail(value: string): string {
  return value.trim().toLowerCase();
}

export const authService = {
  async login(
    email: string,
    password: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    const formData = new URLSearchParams({ username: email, password });
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData.toString(),
      signal: options.signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail ?? 'Login failed');
    }
    const { access_token } = await res.json();
    apiClient.setToken(access_token);
  },

  async register(
    email: string,
    password: string,
    name: string,
    confirmations: PolicyConfirmationsValue,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/register', {
      email,
      password,
      name,
      adult_attested: confirmations.adultAttested,
      terms_accepted: confirmations.termsAccepted,
    }, options);
    await authService.login(email, password, options);
  },

  async logout(): Promise<void> {
    apiClient.clearToken();
  },

  async deleteAccount(
    confirmEmail: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.deleteWithBody('/users/me/account', {
      confirm_email: normalizeAccountEmail(confirmEmail),
    }, options);
    apiClient.clearToken();
  },

  async getSession(): Promise<{ access_token: string } | null> {
    const token = apiClient.getToken();
    return token ? { access_token: token } : null;
  },

  async getCurrentUser(options: ApiRequestOptions = {}): Promise<UserProfile | null> {
    try {
      return await apiClient.get<UserProfile>('/users/me', options);
    } catch {
      return null;
    }
  },

  async updateProfile(
    updates: Partial<UserProfile>,
    options: ApiRequestOptions = {},
  ): Promise<UserProfile> {
    return apiClient.patch<UserProfile>('/users/me', updates, options);
  },

  async requestVerifyEmail(
    email: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/request-verify-token', { email }, options);
  },

  async requestPasswordReset(
    email: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/forgot-password', {
      email: normalizeAccountEmail(email),
    }, options);
  },

  async resetPassword(
    token: string,
    password: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, password }, options);
  },

  async activateImportedAccount(
    token: string,
    password: string,
    confirmations: PolicyConfirmationsValue,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/activate-imported-account', {
      token,
      password,
      adult_attested: confirmations.adultAttested,
      terms_accepted: confirmations.termsAccepted,
    }, options);
  },

  async getPolicyStatus(options: ApiRequestOptions = {}): Promise<PolicyStatus> {
    return apiClient.get<PolicyStatus>('/users/me/policy-status', options);
  },

  async acceptCurrentPolicy(options: ApiRequestOptions = {}): Promise<PolicyStatus> {
    return apiClient.post<PolicyStatus>('/users/me/policy-acceptance', {
      adult_attested: true,
      terms_accepted: true,
    }, options);
  },

  async changePassword(
    currentPassword: string,
    newPassword: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }, options);
  },

  async verifyEmail(
    token: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.post('/auth/verify', { token }, options);
  },
};

export default authService;
